// Single source of truth for Arabic search normalization, shared by the live
// query path (utils.processInputToArabic) and the dictionary index
// (dictionary-db). Kept dependency-free so both can import it without a cycle.
// Uses \u escapes throughout (literal Arabic in regex ranges / comparisons is
// byte-fragile across editors).

// Strip tashkeel/diacritics (U+0610-061A, U+064B-065F), superscript alef (U+0670),
// and tatweel/kashida (U+0640).
export function stripHarakat(str) {
  return (str || "").replace(/[ؐ-ًؚ-ٰٟـ]/g, "");
}

// Other encodings of the standard letters -> the letters the dictionary uses. NFKC
// turns presentation forms (U+FB50-FDFF, U+FE70-FEFF, e.g. pasted ﻛﺘﺎﺏ) into plain
// letters; then Persian/Urdu keyboard letters, which look like Arabic ones but are
// different code points: keheh U+06A9 / swash kaf U+06AA -> kaf U+0643; farsi yeh
// U+06CC, yeh U+06D0, yeh barree U+06D2 -> yaa U+064A; heh goal U+06C1 and heh
// doachashmee U+06BE -> heh U+0647; teh marbuta goal U+06C3 -> teh marbuta U+0629.
// Farsi yeh is those keyboards' only yeh, so at the end of a word it stands for ى
// as often as for ي: it becomes ى there, which lookups and the root analyzer also
// try as ي (see maqsuraFallbackKey), while a ي is never read as ى.
// Hamza seats and alef maqsura are kept: the root analyzer needs them.
const LETTER_VARIANTS = {
  "\u06A9": "\u0643",
  "\u06AA": "\u0643",
  "\u06CC": "\u064A",
  "\u06D0": "\u064A",
  "\u06D2": "\u064A",
  "\u06C1": "\u0647",
  "\u06BE": "\u0647",
  "\u06C3": "\u0629",
};
const LETTER_VARIANT_RE = /[\u06A9\u06AA\u06CC\u06D0\u06D2\u06C1\u06BE\u06C3]/g;
// A farsi yeh with no letter after it (harakat and tatweel don't count).
const FINAL_FARSI_YEH = /\u06CC(?![\u0610-\u061A\u064B-\u065F\u0670\u0640]*[\u0621-\u064A\u066E-\u06D3])/g;
export function foldLetterVariants(str) {
  return (str || "")
    .normalize("NFKC")
    .replace(FINAL_FARSI_YEH, "\u0649")
    .replace(LETTER_VARIANT_RE, (c) => LETTER_VARIANTS[c]);
}

// Collapse hamza-carrier letters so different seats of the same hamza match the
// dictionary's keys. Note hamza-on-yaa (U+0626 ئ) -> alef, NOT yaa: the dataset
// indexes it as alef (e.g. يئس is stored as ياس), so mapping it to yaa was why
// such roots weren't found.
export function normalizeArabicChar(ch) {
  // Any hamza form -> ا: bare ء(0621), أ(0623) إ(0625) آ(0622) ٱ(0671), ئ(0626).
  // (The dataset already stores these roots with alef, e.g. أسف->اسف, يئس->ياس.)
  if (ch === "ء" || ch === "أ" || ch === "إ" || ch === "آ" || ch === "ٱ") return "ا";
  if (ch === "ؤ") return "و"; // ؤ -> و
  if (ch === "ئ") return "ا"; // ئ -> ا
  return ch;
}

// Alef maqsura stays distinct from yaa in keys: the dictionary keys particles and
// names with it (متى, إلى as الى, يحيى), and folding ى into ي made words that end in
// ي open those entries (آلي "automatic" opened إلى). But it keys most defective
// roots with ي (رمي), while people type the citation form رمى. So a key of at most
// three letters that ends in ى, i.e. a root or particle as typed, may also be
// looked up with ي (null when there's no such spelling). Never the other way, and
// never for longer words: كبرى must not open the unrelated كبري.
export function maqsuraFallbackKey(key) {
  const k = key || "";
  if (!k.endsWith("\u0649") || [...k].length > 3) return null;
  return k.slice(0, -1) + "\u064A";
}

// Anything outside the Arabic letter ranges (basic U+0621-064A + extended
// U+0671-06D3): commas, digits, stray punctuation.
const NON_LETTER = /[^ء-يٱ-ۓ]/g;

// Normalize one root token to its search key: fold letter variants, drop harakat,
// collapse hamza carriers, then strip any non-letter so commas/tatweel/marks don't
// pollute keys.
export function normalizeArabicWord(token) {
  const stripped = stripHarakat(foldLetterVariants(token));
  let out = "";
  for (const ch of stripped) out += normalizeArabicChar(ch);
  return out.replace(NON_LETTER, "");
}

// A root may list alternates separated by "/", spaces, commas, or stray bidi
// marks, e.g. "جلو / جلى". Split and return the unique normalized key for each.
// Separators are built from code points (literal bidi/comma chars are byte-fragile):
// "/", ASCII comma, Arabic comma U+060C, Arabic semicolon U+061B, bidi U+200E-200F,
// U+202A-202E, U+2066-2069.
const VARIANT_SPLIT = new RegExp(
  "[\\s/," +
    [0x060c, 0x061b, 0x200e, 0x200f, 0x202a, 0x202b, 0x202c, 0x202d, 0x202e, 0x2066, 0x2067, 0x2068, 0x2069]
      .map((c) => String.fromCharCode(c))
      .join("") +
    "]+"
);
export function rootVariantKeys(root) {
  const keys = [];
  for (const tok of (root || "").split(VARIANT_SPLIT)) {
    const k = normalizeArabicWord(tok);
    if (k.length >= 1 && !keys.includes(k)) keys.push(k);
  }
  return keys;
}

// Union an entry's existing searchableRoots with the keys derived from its root
// field. Existing tokens are re-normalized the same way as queries, so stray
// punctuation tokens ("," "،"), tatweel and bare-hamza spellings never become
// index keys that a typed query can't produce (or that autocomplete would offer).
export function mergeSearchableRoots(existing, root) {
  const out = [];
  for (const tok of [...(Array.isArray(existing) ? existing : []), root]) {
    for (const k of rootVariantKeys(tok)) {
      if (!out.includes(k)) out.push(k);
    }
  }
  return out;
}
